import { createAsyncThunk, createSlice, type PayloadAction } from '@reduxjs/toolkit'
import {
  executeChargingPlan,
  fetchChargingFeedbackMessages,
  fetchCalculationData,
  fetchTelemetryMessages,
  startCharging,
  stopCharging,
} from '../../service/charging-steering-api'
import type {
  ChargingFeedbackMessage,
  ChargingFeedbackMessagesResponse,
  ChargingAction,
  ChargingPlanRequest,
  VehiclePlanCalculationDataResponse,
  VehicleTelemetryMessage,
  VehicleTelemetryMessagesResponse,
} from '../../types/charging-steering.types'
import type { AppError } from '../../types/error.types'
import { toAppError } from '../../utils/ErrorHandler'
import type { RootState } from '../../store'
import { getTelemetryDefaultFromValue, toTelemetryFromQuery } from './utils/telemetryDate.utils'
import { formatAllowedChargingPower } from './utils/chargingPlan.utils'

export type PlanStepDraft = {
  duration: string
  chargePower: string
}

export type ChargingSteeringState = {
  vin: string
  actionLoading: ChargingAction | null
  calculationLoading: boolean
  telemetryLoading: boolean
  chargingFeedbackLoading: boolean
  executeLoading: boolean
  lastCommandMessage?: string
  lastExecutionId?: string
  lastResponse?: {
    title: string
    description: string
  }
  calculationData?: VehiclePlanCalculationDataResponse
  telemetryFrom: string
  telemetryVin?: string
  telemetryMessages: VehicleTelemetryMessage[]
  telemetryRequestId?: string
  telemetryError?: AppError
  chargingFeedbackVin?: string
  chargingFeedbackMessages: ChargingFeedbackMessage[]
  chargingFeedbackRequestId?: string
  chargingFeedbackError?: AppError
  error?: AppError
  planDraft: {
    start: string
    steps: PlanStepDraft[]
  }
}

const createEmptyStep = (): PlanStepDraft => ({ duration: '', chargePower: '' })

const getNowForDatetimeLocal = (): string => {
  const now = new Date()
  now.setSeconds(0, 0)
  const tzOffsetMs = now.getTimezoneOffset() * 60_000
  return new Date(now.getTime() - tzOffsetMs).toISOString().slice(0, 16)
}

const initialState: ChargingSteeringState = {
  vin: '',
  actionLoading: null,
  calculationLoading: false,
  telemetryLoading: false,
  chargingFeedbackLoading: false,
  executeLoading: false,
  telemetryFrom: getTelemetryDefaultFromValue(),
  telemetryMessages: [],
  chargingFeedbackMessages: [],
  planDraft: {
    start: getNowForDatetimeLocal(),
    steps: [createEmptyStep()],
  },
}

export const startChargingThunk = createAsyncThunk<
  { message: string; status: string },
  undefined,
  { state: RootState; rejectValue: AppError }
>('chargingSteering/startCharging', async (_, { getState, rejectWithValue }) => {
  const vin = getState().chargingSteering.vin.trim()

  try {
    const response = await startCharging(vin)
    return { message: response.message, status: response.status }
  } catch (error) {
    return rejectWithValue(toAppError(error, { fallbackMessage: 'Failed to start charging.' }))
  }
})

export const stopChargingThunk = createAsyncThunk<
  { message: string; status: string },
  undefined,
  { state: RootState; rejectValue: AppError }
>('chargingSteering/stopCharging', async (_, { getState, rejectWithValue }) => {
  const vin = getState().chargingSteering.vin.trim()

  try {
    const response = await stopCharging(vin)
    return { message: response.message, status: response.status }
  } catch (error) {
    return rejectWithValue(toAppError(error, { fallbackMessage: 'Failed to stop charging.' }))
  }
})

export const fetchCalculationDataThunk = createAsyncThunk<
  VehiclePlanCalculationDataResponse,
  undefined,
  { state: RootState; rejectValue: AppError }
>('chargingSteering/fetchCalculationData', async (_, { getState, rejectWithValue }) => {
  const vin = getState().chargingSteering.vin.trim()

  try {
    return await fetchCalculationData(vin)
  } catch (error) {
    return rejectWithValue(
      toAppError(error, { fallbackMessage: 'Failed to update calculation data.' }),
    )
  }
})

export const fetchTelemetryMessagesThunk = createAsyncThunk<
  VehicleTelemetryMessagesResponse,
  undefined,
  { state: RootState; rejectValue: AppError }
>('chargingSteering/fetchTelemetryMessages', async (_, { getState, rejectWithValue }) => {
  const { vin, telemetryFrom } = getState().chargingSteering
  const from = toTelemetryFromQuery(telemetryFrom)

  if (!from) {
    return rejectWithValue({
      code: 'INVALID_TELEMETRY_FROM',
      message: 'Please select a valid telemetry from time.',
    })
  }

  try {
    return await fetchTelemetryMessages(vin.trim(), from)
  } catch (error) {
    return rejectWithValue(
      toAppError(error, { fallbackMessage: 'Failed to fetch telemetry messages.' }),
    )
  }
})

export const fetchChargingFeedbackMessagesThunk = createAsyncThunk<
  ChargingFeedbackMessagesResponse,
  undefined,
  { state: RootState; rejectValue: AppError }
>('chargingSteering/fetchChargingFeedbackMessages', async (_, { getState, rejectWithValue }) => {
  const { vin, telemetryFrom } = getState().chargingSteering
  const from = toTelemetryFromQuery(telemetryFrom)

  if (!from) {
    return rejectWithValue({
      code: 'INVALID_MESSAGE_FEED_FROM',
      message: 'Please select a valid message feed from time.',
    })
  }

  try {
    return await fetchChargingFeedbackMessages(vin.trim(), from)
  } catch (error) {
    return rejectWithValue(
      toAppError(error, { fallbackMessage: 'Failed to fetch charging feedback messages.' }),
    )
  }
})

export const executeChargingPlanThunk = createAsyncThunk<
  { id: string; status: string },
  ChargingPlanRequest,
  { state: RootState; rejectValue: AppError }
>('chargingSteering/executeChargingPlan', async (request, { getState, rejectWithValue }) => {
  const vin = getState().chargingSteering.vin.trim()

  try {
    const response = await executeChargingPlan(vin, request)
    return { id: response.id, status: response.status ?? 'ACCEPTED' }
  } catch (error) {
    return rejectWithValue(
      toAppError(error, { fallbackMessage: 'Failed to execute charging plan.' }),
    )
  }
})

const chargingSteeringSlice = createSlice({
  name: 'chargingSteering',
  initialState,
  reducers: {
    setVin(state, action: PayloadAction<string>) {
      const nextVin = action.payload.toUpperCase()

      if (state.vin === nextVin) {
        return
      }

      state.vin = nextVin
      state.telemetryFrom = getTelemetryDefaultFromValue()
      state.telemetryLoading = false
      state.telemetryMessages = []
      state.telemetryVin = undefined
      state.telemetryRequestId = undefined
      state.telemetryError = undefined
      state.chargingFeedbackLoading = false
      state.chargingFeedbackMessages = []
      state.chargingFeedbackVin = undefined
      state.chargingFeedbackRequestId = undefined
      state.chargingFeedbackError = undefined
    },
    setPlanDraftStart(state, action: PayloadAction<string>) {
      state.planDraft.start = action.payload
    },
    setTelemetryFrom(state, action: PayloadAction<string>) {
      state.telemetryFrom = action.payload
    },
    setPlanDraftStep(
      state,
      action: PayloadAction<{ index: number; field: keyof PlanStepDraft; value: string }>,
    ) {
      const { index, field, value } = action.payload
      const step = state.planDraft.steps[index]
      if (!step) return
      step[field] = value
    },
    addPlanDraftStep(state) {
      state.planDraft.steps.push(createEmptyStep())
    },
    removePlanDraftStep(state, action: PayloadAction<number>) {
      if (state.planDraft.steps.length <= 1) return
      state.planDraft.steps = state.planDraft.steps.filter((_, index) => index !== action.payload)
    },
    clearChargingSteeringError(state) {
      state.error = undefined
    },
    clearTelemetryError(state) {
      state.telemetryError = undefined
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(startChargingThunk.pending, (state) => {
        state.actionLoading = 'start'
        state.lastCommandMessage = undefined
        state.lastResponse = undefined
        state.error = undefined
      })
      .addCase(startChargingThunk.fulfilled, (state, action) => {
        state.actionLoading = null
        state.lastCommandMessage = action.payload.message
        state.lastResponse = {
          title: 'Start charging response',
          description: `${action.payload.message} (status: ${action.payload.status})`,
        }
      })
      .addCase(startChargingThunk.rejected, (state, action) => {
        state.actionLoading = null
        state.error = action.payload
        state.lastResponse = {
          title: 'Start charging error',
          description: action.payload?.message ?? 'Failed to start charging.',
        }
      })
      .addCase(stopChargingThunk.pending, (state) => {
        state.actionLoading = 'stop'
        state.lastCommandMessage = undefined
        state.lastResponse = undefined
        state.error = undefined
      })
      .addCase(stopChargingThunk.fulfilled, (state, action) => {
        state.actionLoading = null
        state.lastCommandMessage = action.payload.message
        state.lastResponse = {
          title: 'Stop charging response',
          description: `${action.payload.message} (status: ${action.payload.status})`,
        }
      })
      .addCase(stopChargingThunk.rejected, (state, action) => {
        state.actionLoading = null
        state.error = action.payload
        state.lastResponse = {
          title: 'Stop charging error',
          description: action.payload?.message ?? 'Failed to stop charging.',
        }
      })
      .addCase(fetchCalculationDataThunk.pending, (state) => {
        state.calculationLoading = true
        state.lastResponse = undefined
        state.error = undefined
      })
      .addCase(fetchCalculationDataThunk.fulfilled, (state, action) => {
        state.calculationLoading = false
        state.calculationData = action.payload
        state.lastResponse = {
          title: 'Calculation data response',
          description: `VIN: ${action.payload.vin}, allowed charging power: ${formatAllowedChargingPower(
            action.payload.allowedChargingPower,
          )}, total energy required: ${action.payload.totalEnergyRequired}`,
        }
      })
      .addCase(fetchCalculationDataThunk.rejected, (state, action) => {
        state.calculationLoading = false
        state.error = action.payload
        state.lastResponse = {
          title: 'Calculation data error',
          description: action.payload?.message ?? 'Failed to update calculation data.',
        }
      })
      .addCase(fetchTelemetryMessagesThunk.pending, (state, action) => {
        state.telemetryLoading = true
        state.telemetryRequestId = action.meta.requestId
        state.telemetryError = undefined
      })
      .addCase(fetchTelemetryMessagesThunk.fulfilled, (state, action) => {
        if (state.telemetryRequestId !== action.meta.requestId) {
          return
        }

        if (action.payload.vin.trim().toUpperCase() !== state.vin.trim().toUpperCase()) {
          return
        }

        state.telemetryLoading = false
        state.telemetryRequestId = undefined
        state.telemetryVin = action.payload.vin
        state.telemetryMessages = action.payload.messages
      })
      .addCase(fetchTelemetryMessagesThunk.rejected, (state, action) => {
        if (state.telemetryRequestId !== action.meta.requestId) {
          return
        }

        state.telemetryLoading = false
        state.telemetryRequestId = undefined
        state.telemetryError = action.payload
        state.telemetryMessages = []
      })
      .addCase(fetchChargingFeedbackMessagesThunk.pending, (state, action) => {
        state.chargingFeedbackLoading = true
        state.chargingFeedbackRequestId = action.meta.requestId
        state.chargingFeedbackError = undefined
      })
      .addCase(fetchChargingFeedbackMessagesThunk.fulfilled, (state, action) => {
        if (state.chargingFeedbackRequestId !== action.meta.requestId) {
          return
        }

        if (action.payload.vin.trim().toUpperCase() !== state.vin.trim().toUpperCase()) {
          return
        }

        state.chargingFeedbackLoading = false
        state.chargingFeedbackRequestId = undefined
        state.chargingFeedbackVin = action.payload.vin
        state.chargingFeedbackMessages = action.payload.statuses
      })
      .addCase(fetchChargingFeedbackMessagesThunk.rejected, (state, action) => {
        if (state.chargingFeedbackRequestId !== action.meta.requestId) {
          return
        }

        state.chargingFeedbackLoading = false
        state.chargingFeedbackRequestId = undefined
        state.chargingFeedbackError = action.payload
        state.chargingFeedbackMessages = []
      })
      .addCase(executeChargingPlanThunk.pending, (state) => {
        state.executeLoading = true
        state.lastResponse = undefined
        state.error = undefined
      })
      .addCase(executeChargingPlanThunk.fulfilled, (state, action) => {
        state.executeLoading = false
        state.lastExecutionId = action.payload.id
        state.lastResponse = {
          title: 'Charging plan response',
          description: `Execution ID: ${action.payload.id} (status: ${action.payload.status})`,
        }
      })
      .addCase(executeChargingPlanThunk.rejected, (state, action) => {
        state.executeLoading = false
        state.error = action.payload
        state.lastResponse = {
          title: 'Charging plan error',
          description: action.payload?.message ?? 'Failed to execute charging plan.',
        }
      })
  },
})

export const {
  setVin,
  setPlanDraftStart,
  setTelemetryFrom,
  setPlanDraftStep,
  addPlanDraftStep,
  removePlanDraftStep,
  clearChargingSteeringError,
  clearTelemetryError,
} = chargingSteeringSlice.actions

export const chargingSteeringReducer = chargingSteeringSlice.reducer
